<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { sendPasswordResetEmail } from 'firebase/auth'
import { auth } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import FosInput from '@/components/ui/FosInput.vue'
import FosButton from '@/components/ui/FosButton.vue'

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const showPassword = ref(false)

const canSignIn = computed(() => email.value.trim() !== '' && password.value !== '')

async function handleLogin() {
  try {
    await authStore.login(email.value, password.value)
    const redirect = route.query.redirect as string | undefined
    if (authStore.userProfile?.agency_id) {
      router.push(redirect || '/incidents')
    } else {
      router.push({ name: 'onboarding', query: redirect ? { redirect } : undefined })
    }
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'An error occurred'
    toast.add(msg, 'error')
  }
}

async function handleGoogleLogin() {
  try {
    await authStore.loginWithGoogle()
    const redirect = route.query.redirect as string | undefined
    if (authStore.userProfile?.agency_id) {
      router.push(redirect || '/incidents')
    } else {
      router.push({ name: 'onboarding', query: redirect ? { redirect } : undefined })
    }
  } catch (err: unknown) {
    const code = (err as { code?: string }).code
    if (code === 'auth/popup-closed-by-user') return
    const msg = err instanceof Error ? err.message : 'An error occurred'
    toast.add(msg, 'error')
  }
}

onMounted(() => {
  if (route.query.deactivated === '1') {
    toast.add('Your account has been deactivated. Contact your agency admin.', 'warning', 6000)
  }
})

async function handleForgotPassword() {
  if (!email.value.trim()) return
  try {
    await sendPasswordResetEmail(auth, email.value)
    toast.add('Check your email for reset instructions.', 'default', 5000)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'An error occurred'
    toast.add(msg, 'error')
  }
}
</script>

<template>
  <div class="h-full flex flex-col">
    <header class="h-12 flex items-center justify-between pr-4 bg-background border-b border-border shrink-0">
      <div class="flex items-center h-full overflow-hidden">
        <div class="w-20 flex items-center justify-center gap-2 shrink-0">
          <div class="w-[24px] shrink-0" />
          <img src="/assets/logo.svg" alt="First on Scene" class="h-8 w-auto shrink-0" />
        </div>
        <div class="h-4 w-px bg-border shrink-0" />
        <div class="flex items-center gap-2.5 pl-2.5 overflow-hidden">
          <span class="font-headline font-bold text-sm text-on-surface uppercase tracking-tight truncate">
            First On Scene
          </span>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <ThemeToggle />
        <UserAvatar />
      </div>
    </header>
    <div class="flex-1 overflow-y-auto flex items-center justify-center px-4 py-10 sm:px-6 sm:py-20">
      <div class="border border-border rounded-sm max-w-md w-full flex flex-col items-start gap-8 bg-surface-container p-6 sm:p-10">
        <h1 class="text-2xl font-bold text-on-surface uppercase tracking-wide w-full">Welcome Back</h1>

        <button
          class="rounded w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-surface-container-high text-on-surface text-sm font-medium hover:bg-surface-container-highest transition-colors"
          @click="handleGoogleLogin"
        >
          <svg class="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Sign in with Google
        </button>

        <div class="flex items-center gap-2 w-full">
          <div class="h-px flex-1 bg-outline-variant/15" />
          <span class="text-outline font-bold text-xs uppercase tracking-widest">or</span>
          <div class="h-px flex-1 bg-outline-variant/15" />
        </div>

        <div class="flex flex-col gap-5 w-full">
          <div class="flex flex-col gap-1.5">
            <label for="email" class="text-[10px] font-label uppercase tracking-widest text-outline">Email Address</label>
            <FosInput id="email" v-model="email" type="email" placeholder="you@example.com" />
          </div>
          <div class="flex flex-col gap-1.5">
            <label for="password" class="text-[10px] font-label uppercase tracking-widest text-outline">Password</label>
            <FosInput
              id="password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              placeholder="Password"
              @keydown.enter="handleLogin"
            />
            <button
              class="self-start text-xs text-outline hover:text-on-surface-variant transition-colors mt-0.5"
              type="button"
              @click="showPassword = !showPassword"
            >
              {{ showPassword ? 'Hide' : 'Show' }} password
            </button>
          </div>
        </div>

        <div class="flex justify-end w-full -mt-4">
          <button
            class="text-outline text-xs hover:text-on-surface-variant transition-colors"
            @click="handleForgotPassword"
          >Forgot your password?</button>
        </div>

        <FosButton variant="primary" class="w-full" :disabled="!canSignIn" @click="handleLogin">
          Sign In
        </FosButton>
      </div>
    </div>
  </div>
</template>
